<section class="min-h-screen px-4 py-16 text-white">
  <div class="max-w-7xl mx-auto">
    <app-admin-page-header>
      Gestión de <span class="text-cyan-400">Productos</span>
    </app-admin-page-header>

    <!-- 🛠 Botón de creación -->
    <div class="mb-8 text-right">
      <button
        (click)="goToAddProductForm()"
        appGalaxyButton
        galaxyVariant="primary"
        galaxySize="md"
        appGalaxyRipple
      >
        + Crear producto
      </button>
    </div>

    <!-- 📊 Contenedor principal -->
    <app-galaxy-card padding="none">
      <div galaxy-card-content>
        <!-- ⏳ Indicador de carga -->
        @if (isLoading$ | async) {
          <div>
            <!-- Skeleton para Tabla (Desktop) -->
            <div class="overflow-x-auto hidden sm:block p-6">
              @for (i of [1, 2, 3, 4, 5]; track i) {
                <div
                  class="flex items-center space-x-6 py-4 border-b border-white/10"
                >
                  <app-galaxy-skeleton
                    height="1.25rem"
                    width="15%"
                  ></app-galaxy-skeleton>
                  <app-galaxy-skeleton
                    height="1.25rem"
                    width="30%"
                  ></app-galaxy-skeleton>
                  <app-galaxy-skeleton
                    height="1.25rem"
                    width="15%"
                  ></app-galaxy-skeleton>
                  <app-galaxy-skeleton
                    height="1.25rem"
                    width="15%"
                    class="mx-auto"
                  ></app-galaxy-skeleton>
                  <app-galaxy-skeleton
                    height="2rem"
                    width="20%"
                    borderRadius="9999px"
                  ></app-galaxy-skeleton>
                </div>
              }
            </div>
            <!-- Skeleton para Tarjetas (Móvil) -->
            <div class="sm:hidden space-y-4 p-4">
              @for (i of [1, 2, 3, 4, 5]; track i) {
                <app-galaxy-card>
                  <div galaxy-card-content class="p-4 space-y-3">
                    <app-galaxy-skeleton
                      height="1.5rem"
                      width="70%"
                    ></app-galaxy-skeleton>
                    <app-galaxy-skeleton
                      height="1rem"
                      width="40%"
                    ></app-galaxy-skeleton>
                    <div class="flex justify-end pt-2">
                      <app-galaxy-skeleton
                        height="2rem"
                        width="5rem"
                        borderRadius="9999px"
                      ></app-galaxy-skeleton>
                    </div>
                  </div>
                </app-galaxy-card>
              }
            </div>
          </div>
        }

        <!-- Mensaje de no hay productos -->
        @if (!(isLoading$ | async) && (products$ | async)?.length === 0) {
          <div class="text-center py-24 px-6">
            <p class="text-xl text-gray-400">No hay productos para mostrar.</p>
          </div>
        }

        <div [class.blur-sm]="isLoading$ | async">
          <!-- Tabla de productos (visible en sm y superior) -->
          <div class="overflow-x-auto hidden sm:block">
            <table class="min-w-full text-sm text-white table-auto">
              <thead>
                <tr class="text-left text-gray-400 border-b border-white/10">
                  <th class="py-4 px-6 font-semibold">Código</th>
                  <th class="py-4 px-6 font-semibold">Nombre</th>
                  <th class="py-4 px-6 font-semibold">Precio</th>
                  <th class="py-4 px-6 font-semibold text-center">Estado</th>
                  <th class="py-4 px-6 font-semibold text-center">Acciones</th>
                </tr>
              </thead>
              <tbody appAnimateStagger>
                @for (product of products$ | async; track product) {
                  <tr
                    #staggerItem
                    class="border-b border-white/10 hover:bg-white/5 transition duration-200"
                    [class.opacity-60]="!product.active"
                  >
                    <td class="py-3 px-6 text-gray-300">{{ product.code }}</td>
                    <td class="py-3 px-6 text-gray-100">{{ product.name }}</td>
                    <td class="py-3 px-6 text-gray-200">
                      ${{ product.price }}
                    </td>
                    <td class="py-3 px-6 text-center">
                      <span
                        [ngClass]="{
                          'bg-green-500/80': product.active,
                          'bg-red-500/80': !product.active,
                        }"
                        class="text-white px-3 py-1 rounded-full text-xs font-medium shadow-neumorphic-dark-inset"
                      >
                        {{ product.active ? "Activo" : "Inactivo" }}
                      </span>
                    </td>
                    <td class="py-3 px-6 text-center space-x-2">
                      <button
                        [routerLink]="['/admin/product/update', product.id]"
                        appGalaxyButton
                        galaxyVariant="secondary"
                        galaxySize="sm"
                      >
                        Editar
                      </button>
                      @if (product.active) {
                        <button
                          (click)="deleteProductById(product.id)"
                          appGalaxyButton
                          galaxyVariant="ghost"
                          galaxySize="sm"
                        >
                          Desactivar
                        </button>
                      }
                      @if (!product.active) {
                        <button
                          (click)="reactivateProductById(product.id)"
                          appGalaxyButton
                          galaxyVariant="secondary"
                          galaxySize="sm"
                        >
                          Reactivar
                        </button>
                      }
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>

          <!-- Tarjetas de productos (visible en móvil) -->
          <div class="sm:hidden space-y-4 p-4" appAnimateStagger>
            @for (product of products$ | async; track product) {
              <app-galaxy-card
                #staggerItem
                [class.opacity-60]="!product.active"
              >
                <div galaxy-card-content class="p-4 space-y-3">
                  <div class="flex justify-between items-center text-lg">
                    <span class="font-bold text-cyan-300">{{
                      product.name
                    }}</span>
                    <span class="font-mono text-gray-200"
                      >${{ product.price }}</span
                    >
                  </div>
                  <div class="text-sm text-gray-400">
                    Código: {{ product.code }}
                  </div>
                  <div class="flex justify-between items-center">
                    <span
                      [ngClass]="{
                        'bg-green-500/80': product.active,
                        'bg-red-500/80': !product.active,
                      }"
                      class="text-white px-3 py-1 rounded-full text-xs font-medium shadow-neumorphic-dark-inset"
                    >
                      {{ product.active ? "Activo" : "Inactivo" }}
                    </span>
                  </div>
                  <div
                    class="flex justify-end gap-2 pt-3 border-t border-white/10"
                  >
                    <button
                      [routerLink]="['/admin/product/update', product.id]"
                      appGalaxyButton
                      galaxyVariant="secondary"
                      galaxySize="sm"
                    >
                      Editar
                    </button>
                    @if (product.active) {
                      <button
                        (click)="deleteProductById(product.id)"
                        appGalaxyButton
                        galaxyVariant="ghost"
                        galaxySize="sm"
                      >
                        Desactivar
                      </button>
                    }
                    @if (!product.active) {
                      <button
                        (click)="reactivateProductById(product.id)"
                        appGalaxyButton
                        galaxyVariant="secondary"
                        galaxySize="sm"
                      >
                        Reactivar
                      </button>
                    }
                  </div>
                </div>
              </app-galaxy-card>
            }
          </div>
        </div>

        <!-- 📄 Paginación -->
        @if ((isLoading$ | async) === false && (totalPages$ | async)! > 1) {
          <div
            class="flex justify-center items-center p-4 border-t border-white/10"
          >
            <div class="flex items-center space-x-2">
              <button
                (click)="goToPreviousPage()"
                [disabled]="(currentPage$ | async) === 0"
                appGalaxyButton
                galaxyVariant="ghost"
                galaxySize="sm"
              >
                Anterior
              </button>
              @if (totalPages$ | async; as totalPages) {
                @for (pageNum of getPageNumbers(totalPages); track pageNum) {
                  <button
                    (click)="onPageChange(pageNum)"
                    [ngClass]="{
                      'galaxy-variant-primary':
                        (currentPage$ | async) === pageNum,
                      'galaxy-variant-ghost':
                        (currentPage$ | async) !== pageNum,
                    }"
                    appGalaxyButton
                    galaxySize="sm"
                  >
                    {{ pageNum + 1 }}
                  </button>
                }
              }
              <button
                (click)="goToNextPage()"
                [disabled]="
                  (currentPage$ | async)! >= ((totalPages$ | async) ?? 0) - 1
                "
                appGalaxyButton
                galaxyVariant="ghost"
                galaxySize="sm"
              >
                Siguiente
              </button>
            </div>
          </div>
        }
      </div>
    </app-galaxy-card>
  </div>
</section>
